import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  HelpCircle, Mail, Phone, MessageCircle, BookOpen, Video,
  FileText, Users, Search, ExternalLink, ArrowLeft
} from 'lucide-react';

const HelpSupportPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        { q: 'How do I create a new CSR project?', a: 'Navigate to your dashboard and click "Create New Project". Fill in the project details in the wizard and submit for approval.' },
        { q: 'How do I match with NGOs?', a: 'Go to "NGO Matching" from your dashboard. Use filters to find NGOs based on cause, geography, and credibility score.' },
        { q: 'Where can I find compliance documents?', a: 'Access the "Compliance Vault" from your dashboard. All your CSR-related documents are automatically generated and stored there.' }
      ]
    },
    {
      category: 'Projects & Milestones',
      questions: [
        { q: 'How do I track project milestones?', a: 'Each project has a "Milestones" tab where you can view progress, update status, and manage deadlines.' },
        { q: 'Can I generate impact reports?', a: 'Yes! Use the "Impact Story Generator" to create compelling narratives, or use the "SROI Calculator" for detailed ROI analysis.' }
      ]
    },
    {
      category: 'Account & Settings',
      questions: [
        { q: 'How do I manage notification preferences?', a: 'Click on your profile icon → Notification Settings. You can customize email alerts, weekly digest, and more.' },
        { q: 'How do I update my company profile?', a: 'Go to Dashboard → Settings → Company Profile to update your information, logo, and CSR focus areas.' }
      ]
    }
  ];

  const resources = [
    { icon: BookOpen, title: 'Knowledge Hub', description: 'Access CSR law updates, templates, and case studies', link: '/knowledge' },
    { icon: Video, title: 'Video Tutorials', description: 'Watch step-by-step guides for all features', link: '#tutorials' },
    { icon: FileText, title: 'Documentation', description: 'Comprehensive guides and API documentation', link: '#docs' },
    { icon: Users, title: 'Community Forum', description: 'Connect with other CSR professionals', link: '#forum' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-28 pb-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-7 h-7 text-foreground" />
            </div>
            <h1 className="heading-1 text-foreground mb-3">How can we help you?</h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              Find answers, watch tutorials, or get in touch with our support team
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <input
                type="text"
                placeholder="Search for help..."
                className="input-base pl-12 py-4 text-base"
              />
            </div>
          </div>

          {/* Quick Contact */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="card-base p-6 text-center hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Email Support</h3>
              <p className="text-sm text-muted-foreground mb-4">support@tmgimpact.in</p>
              <button
                onClick={() => window.location.href = 'mailto:support@tmgimpact.in'}
                className="btn-outline btn-sm"
              >
                Send Email
              </button>
            </div>

            <div className="card-base p-6 text-center hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Phone Support</h3>
              <p className="text-sm text-muted-foreground mb-4">+91-XXX-XXX-XXXX</p>
              <button className="btn-outline btn-sm">Call Now</button>
            </div>

            <div className="card-base p-6 text-center hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Live Chat</h3>
              <p className="text-sm text-muted-foreground mb-4">Available 9 AM - 6 PM IST</p>
              <button className="btn-outline btn-sm">Start Chat</button>
            </div>
          </div>

          {/* Resources */}
          <div className="mb-12">
            <h2 className="heading-3 text-foreground mb-6">Learning Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {resources.map((resource, index) => {
                const Icon = resource.icon;
                return (
                  <button
                    key={index}
                    onClick={() => navigate(resource.link)}
                    className="card-interactive p-6 text-left"
                  >
                    <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{resource.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{resource.description}</p>
                    <span className="text-sm font-medium flex items-center gap-1" style={{ color: 'hsl(30 10% 25%)' }}>
                      Learn more <ExternalLink className="w-3 h-3" />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-12">
            <h2 className="heading-3 text-foreground mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((section, sectionIndex) => (
                <div key={sectionIndex} className="card-base">
                  <div className="p-5 border-b border-border">
                    <h3 className="heading-4 text-foreground">{section.category}</h3>
                  </div>
                  <div className="divide-y divide-border">
                    {section.questions.map((faq, faqIndex) => (
                      <div key={faqIndex} className="p-5">
                        <h4 className="font-semibold text-foreground mb-2">{faq.q}</h4>
                        <p className="text-muted-foreground text-sm">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Still Need Help */}
          <div className="card-base p-8 text-center border-l-4" style={{ borderLeftColor: 'hsl(15 60% 55%)' }}>
            <h2 className="heading-3 text-foreground mb-3">Still need help?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our support team is here to assist you. Send us a message and we'll get back to you within 24 hours.
            </p>
            <button
              onClick={() => window.location.href = 'mailto:support@tmgimpact.in'}
              className="btn-primary btn-lg"
            >
              <Mail className="w-5 h-5" />
              Contact Support Team
            </button>
          </div>

          <div className="mt-8">
            <button 
              onClick={() => navigate(-1)}
              className="btn-ghost text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HelpSupportPage;
